import { useTranslation } from 'react-i18next'
import { PlusIcon, FolderPlusIcon } from '@heroicons/react/24/outline'

/**
 * MealTargetSelector — inline radio group for choosing where to save food entries.
 *
 * Shown after analysis when existing meals of the same type exist for the date.
 * Lets the user pick "Add to [existing meal]" or "Create new meal".
 *
 * Props:
 *   existingMeals  — array of meal objects (id, name, total_calories)
 *   selectedMealId — currently selected meal ID, or null for "create new"
 *   onSelect       — callback(mealId | null)
 */
export default function MealTargetSelector({ existingMeals = [], selectedMealId = null, onSelect }) {
  const { t } = useTranslation()

  if (existingMeals.length === 0) return null

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
      <p className="text-sm font-medium text-amber-800">
        {t('nutrition.mealTarget.title', 'Existing meals found for this type and date')}
      </p>
      <div className="space-y-1.5">
        {/* Create new meal option — default */}
        <label className="flex items-center gap-2 cursor-pointer rounded-md border border-gray-200 bg-surface-container px-3 py-2 hover:border-blue-300 transition-colors">
          <input
            type="radio"
            name="meal-target"
            checked={selectedMealId === null}
            onChange={() => onSelect(null)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
          />
          <PlusIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900">
            {t('nutrition.mealTarget.createNew', 'Create new meal')}
          </span>
        </label>

        {/* Existing meal options */}
        {existingMeals.map((meal) => (
          <label
            key={meal.id}
            className="flex items-center gap-2 cursor-pointer rounded-md border border-gray-200 bg-surface-container px-3 py-2 hover:border-blue-300 transition-colors"
          >
            <input
              type="radio"
              name="meal-target"
              checked={selectedMealId === meal.id}
              onChange={() => onSelect(meal.id)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <FolderPlusIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
            <span className="text-sm text-gray-700">
              {t('nutrition.mealTarget.addToExisting', 'Add to {{name}} ({{calories}} kcal)', {
                name: meal.name || meal.meal_type,
                calories: Math.round(meal.total_calories || 0),
              })}
            </span>
          </label>
        ))}
      </div>
    </div>
  )
}
